import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import api from '@/services/api'
import { DEMO_AT_MODULE_LOAD, resetDemoScope } from '@/config/demoMode'
import { WORKFLOW_META, WORKFLOW_ORDER } from '@/config/workflowModules'
import type { PlatformModuleId } from '@/types'
import { useLocalAuth } from '@/auth/LocalAuthContext'

export interface DemoDataSettings {
  masterEnabled: boolean
  modules: Record<string, boolean>
}

interface DemoDataContextValue {
  loading: boolean
  settings: DemoDataSettings
  activePartition: 'demo' | 'live'
  refresh: () => Promise<void>
  isModuleDemoEnabled: (moduleId: PlatformModuleId) => boolean
  canManageDemoSettings: boolean
  updateSettings: (patch: Partial<DemoDataSettings> & { modules?: Record<string, boolean> }) => Promise<void>
}

const DEFAULT_SETTINGS: DemoDataSettings = {
  masterEnabled: true,
  modules: Object.fromEntries(WORKFLOW_ORDER.map((id) => [id, true])),
}

const DemoDataContext = createContext<DemoDataContextValue | null>(null)

/**
 * Last successfully-loaded settings, mirrored locally.
 *
 * Both the no-token path and the request-failure path used to fall back to
 * DEFAULT_SETTINGS, which has masterEnabled: true. So after an operator turned
 * the illustrative dataset off, any expired token, slow first paint or network
 * blip silently put the sample portfolio back on screen — in front of whoever
 * happened to be watching. Falling back to the last known answer instead keeps
 * "off" meaning off, and only a genuinely fresh install sees demo data.
 */
const CACHE_KEY = 'cmos_demo_settings'

function readCachedSettings(): DemoDataSettings | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY)
    if (!raw) return null
    return normalizeSettings(JSON.parse(raw) as Record<string, unknown>)
  } catch {
    return null
  }
}

function writeCachedSettings(settings: DemoDataSettings) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(settings))
  } catch {
    /* localStorage unavailable — fall back to in-memory only */
  }
}

function liveSettings(): DemoDataSettings {
  return {
    masterEnabled: false,
    modules: Object.fromEntries(WORKFLOW_ORDER.map((id) => [id, false])),
  }
}

function fallbackSettings(environment?: string): DemoDataSettings {
  // Account scope is authoritative. A stale browser cache or a request blocked
  // by the first-login agreement gate must never put a live account into the
  // illustrative portfolio. Only an account KNOWN to be a demo environment may
  // use the last known settings; everything else fails closed/live.
  //
  // `environment && ...` used to let an UNKNOWN environment through to
  // DEFAULT_SETTINGS, which has masterEnabled true. Unknown is exactly the
  // shape of an Azure AD session — no `cmos_token`, so no local user, so no
  // environment — which is how Antarctica signs in. The sample portfolio was
  // therefore the default for the one class of account that must never see it.
  if (environment !== 'demo') return liveSettings()
  return readCachedSettings() ?? DEFAULT_SETTINGS
}

function normalizeSettings(raw: Record<string, unknown> | undefined): DemoDataSettings {
  const modules = { ...DEFAULT_SETTINGS.modules }
  const storedModules = raw?.modules
  if (storedModules && typeof storedModules === 'object') {
    for (const moduleId of WORKFLOW_ORDER) {
      if (moduleId in storedModules) {
        modules[moduleId] = Boolean((storedModules as Record<string, unknown>)[moduleId])
      }
    }
  }
  return {
    masterEnabled: raw?.masterEnabled !== false,
    modules,
  }
}


/**
 * Correct the demo constants a page already froze at module scope.
 *
 * `withDemo()` is evaluated at bundle-evaluation time, before any account is
 * known, so on a cold browser a live account's CRM, Documents, News and
 * notification feed were built from the sample dataset and stayed that way for
 * the whole session — a second full page load was the only thing that fixed it.
 *
 * Once the account IS known, a disagreement with that frozen value is corrected
 * by one reload. Session-guarded, because a reload loop is a worse failure than
 * the one being fixed.
 */
const RECONCILED_KEY = 'cmos_demo_scope_reconciled'

/**
 * Whose demo scope the browser's cache holds. The cache (`cmos_demo_settings`,
 * `cmos_demo_data`) is only an answer for the identity that wrote it.
 */
const IDENTITY_KEY = 'cmos_demo_identity'

/**
 * Who is signed in, as far as the demo scope is concerned: the local account and
 * its environment. An Azure AD session has no local profile, so every one is
 * `anonymous` — which is the fail-closed (live) scope, the same for all of them.
 */
function identityOf(
  user: { id?: string; email?: string; environment?: string } | null | undefined,
  token: string | null,
): string {
  if (!user) return token ? 'token' : 'anonymous'
  return `${user.id ?? user.email ?? 'local'}:${user.environment ?? ''}`
}

/**
 * Reload once per identity if the module-scope constants disagree with the scope.
 *
 * The guard used to be one flag for the whole tab session ("1"), so after the first
 * reconcile no later identity could ever be reconciled: sign out of a demo account,
 * sign in to a live one, and every page that froze its sample dataset at bundle
 * evaluation (CRM, Documents, News, the notification feed) kept showing it. The guard
 * now records WHICH identity was reconciled, so the next identity gets its own reload.
 */
function reconcileModuleScope(settings: DemoDataSettings, identity: string) {
  if (settings.masterEnabled === DEMO_AT_MODULE_LOAD) return
  try {
    if (sessionStorage.getItem(RECONCILED_KEY) === identity) return
    sessionStorage.setItem(RECONCILED_KEY, identity)
  } catch {
    // No sessionStorage means no loop guard, and an unguarded reload is worse
    // than a stale constant. Leave the page as it is.
    return
  }
  window.location.reload()
}

/**
 * The identity changed (sign-out, or another account on this browser): forget the
 * previous account's demo scope before the new one is read. First sight of a browser
 * whose cache has no recorded owner counts as a change too, because a cache with no
 * owner is exactly the stale answer this exists to refuse.
 */
function adoptIdentity(identity: string) {
  try {
    if (localStorage.getItem(IDENTITY_KEY) === identity) return
    resetDemoScope()
    localStorage.setItem(IDENTITY_KEY, identity)
  } catch {
    // Storage is unavailable: there is no cache to be stale.
    resetDemoScope()
  }
}

interface Scope {
  /** Whose answer this is. A scope read for another identity is never served. */
  identity: string
  settings: DemoDataSettings
  partition: 'demo' | 'live'
}

export function DemoDataProvider({ children }: { children: ReactNode }) {
  const { localToken, localUser } = useLocalAuth()
  const environment = localUser?.environment
  const agreementAcceptedAt = localUser?.trial_agreement_accepted_at
  const ndaAcceptedAt = localUser?.nda_accepted_at
  const identity = identityOf(localUser, localToken)
  const [fetching, setFetching] = useState(true)
  const [scope, setScope] = useState<Scope>(() => {
    const initial = fallbackSettings(environment)
    return { identity, settings: initial, partition: initial.masterEnabled ? 'demo' : 'live' }
  })

  // Another identity's scope is not this one's: until it has answered, serve the
  // fail-closed live scope, never the previous account's sample portfolio.
  const sameIdentity = scope.identity === identity
  const settings = sameIdentity ? scope.settings : liveSettings()
  const activePartition = sameIdentity ? scope.partition : 'live'
  const loading = fetching || !sameIdentity

  const canManageDemoSettings = Boolean(localUser?.is_super_admin)

  const refresh = useCallback(async () => {
    const apply = (next: DemoDataSettings, partition: 'demo' | 'live') => {
      setScope({ identity, settings: next, partition })
      // Persist it: `demoEnabled()` reads this cache and would otherwise keep
      // answering from its own platform-storage default, so the two gates could
      // disagree about the same account for the whole session.
      writeCachedSettings(next)
      reconcileModuleScope(next, identity)
    }

    if (!localToken) {
      const cached = fallbackSettings(environment)
      apply(cached, cached.masterEnabled ? 'demo' : 'live')
      setFetching(false)
      return
    }

    // The backend contract says only a demo-environment account may see demo
    // data. Enforce that synchronously in the browser too. This also avoids a
    // first-login race where /settings/demo-data returns 428 until the trial
    // agreement is accepted and the old default (demo=true) leaked through.
    if (environment && environment !== 'demo') {
      apply(liveSettings(), 'live')
      setFetching(false)
      return
    }

    setFetching(true)
    try {
      const res = await api.get<{ data: Record<string, unknown>; activePartition?: 'demo' | 'live' }>(
        '/settings/demo-data',
      )
      apply(normalizeSettings(res.data.data), res.data.activePartition === 'live' ? 'live' : 'demo')
    } catch {
      const cached = fallbackSettings(environment)
      apply(cached, cached.masterEnabled ? 'demo' : 'live')
    } finally {
      setFetching(false)
    }
  // Agreement acceptance does not change the token, but it changes whether
  // this request is allowed. Keep both timestamps as deliberate refresh keys.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agreementAcceptedAt, environment, identity, localToken, ndaAcceptedAt])

  // Before the new identity's scope is read (the effect below), the old one's is
  // forgotten. Effects run in order, so this one precedes the refresh.
  useEffect(() => {
    adoptIdentity(identity)
  }, [identity])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const isModuleDemoEnabled = useCallback(
    (moduleId: PlatformModuleId) => {
      if (environment && environment !== 'demo') return false
      if (!settings.masterEnabled) return false
      return Boolean(settings.modules[moduleId])
    },
    [environment, settings],
  )

  const updateSettings = useCallback(
    async (patch: Partial<DemoDataSettings> & { modules?: Record<string, boolean> }) => {
      if (!canManageDemoSettings) {
        throw new Error('Super admin access required')
      }
      const body: Record<string, unknown> = {}
      if (patch.masterEnabled !== undefined) body.masterEnabled = patch.masterEnabled
      if (patch.modules) body.modules = patch.modules
      const res = await api.patch<{ data: Record<string, unknown>; activePartition?: 'demo' | 'live' }>(
        '/settings/demo-data',
        body,
      )
      const next = normalizeSettings(res.data.data)
      const effective = environment && environment !== 'demo' ? liveSettings() : next
      setScope({
        identity,
        settings: effective,
        partition:
          environment && environment !== 'demo'
            ? 'live'
            : res.data.activePartition === 'live'
              ? 'live'
              : 'demo',
      })
      writeCachedSettings(effective)
    },
    [canManageDemoSettings, environment, identity],
  )

  const value = useMemo<DemoDataContextValue>(
    () => ({
      loading,
      settings,
      activePartition,
      refresh,
      isModuleDemoEnabled,
      canManageDemoSettings,
      updateSettings,
    }),
    [loading, settings, activePartition, refresh, isModuleDemoEnabled, canManageDemoSettings, updateSettings],
  )

  return <DemoDataContext.Provider value={value}>{children}</DemoDataContext.Provider>
}

export function useDemoDataSettings() {
  const ctx = useContext(DemoDataContext)
  if (!ctx) {
    throw new Error('useDemoDataSettings must be used within DemoDataProvider')
  }
  return ctx
}

export function getModuleLabel(moduleId: PlatformModuleId) {
  return WORKFLOW_META[moduleId]?.label ?? moduleId
}
